import { Box, HStack, IconButton, useBoolean } from "@chakra-ui/react";
import { Avatar } from "@components/ui-kit";
import { t } from "@lingui/macro";
import { useCreateTaskCommentMutation } from "@src/__generated__/graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import {
  EMPTY_EDITOR_CONTENT,
  EMPTY_EDITOR_LENGTH,
  TextEditor,
  type TextEditorProps,
  type TextEditorRef,
} from "@src/components/ui-kit/TextEditor/TextEditor";
import { CommentModel } from "@src/components/widgets/Modals/ModalCommunication/models/CommentModel";
import { trackEvent } from "@src/services/amplitude";
import { onError } from "@src/utils/apollo";
import { useStore } from "@src/utils/hooks";
import { useInsideClick } from "@src/utils/hooks/useInsideClick";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import { observer } from "mobx-react-lite";
import { useRef } from "react";

interface CommentInputProps {
  taskId: string;
}

const CommentInput = ({ taskId }: CommentInputProps) => {
  const { authStore, taskDetailModalStore, UIStore } = useStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<TextEditorRef>(null);
  const [isFocused, setIsFocused] = useBoolean(false);
  const mentionedUserIds = useRef<string[]>([]);
  const mentionedTeamIds = useRef<string[]>([]);
  const mentionedAssignees = useRef(false);
  const attachmentIds = useRef<string[]>([]);
  const { isMobile } = useScreenType();

  const currentUserScope =
    taskDetailModalStore.communicationTabParentStore.selectedTabInfo.userScope;
  const allowedMentions =
    taskDetailModalStore.communicationTabParentStore.selectedTabInfo
      .userTypesWithAccess;

  useInsideClick({
    ref: taskDetailModalStore.drawerBodyRef,
    handler: () => {
      setIsFocused.off();
    },
  });

  const onChangeTextEditor: TextEditorProps["onChange"] = (
    value,
    {
      mentioned_user_ids,
      attachment_ids,
      mentioned_team_ids,
      mentioned_assignees,
    },
  ) => {
    mentionedUserIds.current = mentioned_user_ids;
    mentionedTeamIds.current = mentioned_team_ids;
    mentionedAssignees.current = mentioned_assignees;
    attachmentIds.current = attachment_ids;

    if (!value) return;
    taskDetailModalStore.handleChangeCommentInputValue(value.toString());
    taskDetailModalStore.setCommentChanged(value.length > EMPTY_EDITOR_LENGTH);
  };

  const [createComment, { loading }] = useCreateTaskCommentMutation({
    onCompleted(data) {
      if (data.createTaskComment) {
        const comment = new CommentModel(data.createTaskComment);
        taskDetailModalStore.newlyCreatedCommentID = comment.id;
        taskDetailModalStore.communicationTabParentStore.handleAddComment(
          comment,
        );
        // scroll to view new comment
        taskDetailModalStore.commentsContainerRef.current?.scrollIntoView();
        trackEvent("task", "Added new comment");
        editorRef.current?.clear();
        taskDetailModalStore.handleChangeCommentInputValue(
          EMPTY_EDITOR_CONTENT,
        );
        taskDetailModalStore.setCommentChanged(false);
      }
    },
    ...onError(),
  });

  const onSave = () => {
    setIsFocused.off();
    createComment({
      variables: {
        input: {
          task_id: taskId,
          body: taskDetailModalStore.commentInputValue,
          files: attachmentIds.current,
          mentioned_user_ids: mentionedUserIds.current,
          mentioned_team_ids: mentionedTeamIds.current,
          mentioned_assignees: mentionedAssignees.current,
          scope: currentUserScope,
        },
      },
    });
  };

  return (
    <HStack
      key={taskDetailModalStore.activeTabId.value}
      align="end"
      w="full"
      spacing="3"
    >
      {!isMobile && (
        <div className="flex-shrink-0">
          <Avatar
            mb="1"
            colorScheme={authStore.user!.profile?.hex_color}
            name={authStore.user!.full_name}
            src={authStore.user!.image?.urls.thumbnail}
          />
        </div>
      )}
      <HStack ref={containerRef} alignItems="start" w={"full"}>
        <Box
          pos="relative"
          flexGrow="1"
          overflowY="auto"
          w="full"
          maxH="400px"
          border="1px solid"
          borderColor="grey.100"
          transition="all .3s ease"
          paddingBlockEnd="1"
          paddingInline="1"
          rounded="xl"
          _hover={{
            borderColor: "violet.500",
          }}
          _focusWithin={{
            borderColor: "violet.500",
          }}
        >
          <TextEditor
            key={allowedMentions.toString()}
            ref={editorRef}
            ml="2"
            showToolbar={isFocused}
            isEditable
            minH="120px"
            placeholder={t`Write comment or @mention to notify...`}
            onChange={onChangeTextEditor}
            initialValue={taskDetailModalStore.commentInputValue}
            attachments={[]}
            taskId={taskId}
            allowedMentions={allowedMentions}
            onPaste={() => {
              taskDetailModalStore.setCommentChanged(true);
            }}
            // INFO: In case some client will want this back
            // enableDrafts
            // draftKey={getTaskDetailDraftKey({
            //   taskId,
            //   editorName: 'comment-input',
            //   tabId: TaskTabIdEnum.Communication,
            // })}
            onFocus={() => setIsFocused.on()}
          />
        </Box>
        <div className="flex-shrink-0">
          <IconButton
            mb="1"
            aria-label={t`Send comment`}
            icon={<Icon name="send-03" variant="solid" w="6" h="6" />}
            isDisabled={!taskDetailModalStore.commentChanged}
            isLoading={loading || UIStore.isEditorUploading}
            onClick={onSave}
            variant="ghost"
          />
        </div>
      </HStack>
    </HStack>
  );
};

export default observer(CommentInput);
